import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, GraduationCap } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { api } from "../lib/api";

const emptyForm = {
  first_name: "", last_name: "", email: "", phone: "",
  country_of_residence: "", city: "", nationality: "", date_of_birth: "",
  education_level: "bachelor", field_of_study: "", institution: "", graduation_year: "",
  years_of_experience: 0, profession: "", professional_summary: "",
  has_passport: false, passport_expiry: "",
  french_level: "", english_level: "",
  target_pathway: "PR", financial_situation: "limited",
  notes: "", consent: true,
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs tracking-[0.2em] uppercase text-navy/50 block mb-1.5">{label}</label>
    {children}
  </div>
);

const inp = "w-full h-11 px-3 rounded-lg border border-navy/15 bg-white focus:border-terracotta focus:outline-none text-sm text-navy";
const sel = inp;
const area = "w-full px-3 py-2 rounded-lg border border-navy/15 bg-white focus:border-terracotta focus:outline-none text-sm text-navy resize-none";

export default function TalentRegistry() {
  const { t, lang } = useLang();
  const f = (k) => t(`registry.fields.${k}`);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return;
    setBusy(true);
    try {
      const payload = {
        ...form,
        years_of_experience: Number(form.years_of_experience) || 0,
        graduation_year: form.graduation_year ? Number(form.graduation_year) : null,
        language: lang,
      };
      await api.post("/registry/talent", payload);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const raw = err.response?.data?.detail;
      const msg = typeof raw === "string" ? raw : t("common.error");
      alert(msg);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="bg-alabaster min-h-[80vh]" data-testid="talent-registry-done">
        <section className="max-w-[900px] mx-auto px-6 lg:px-10 py-24 text-center">
          <p className="font-serif text-8xl text-terracotta leading-none mb-6">✓</p>
          <h1 className="font-serif text-5xl lg:text-6xl text-navy tracking-tight mb-6">{t("registry.done_title")}</h1>
          <p className="text-navy/70 text-lg max-w-lg mx-auto mb-10">{t("registry.done_talent")}</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy text-alabaster text-sm hover:bg-terracotta transition">
            {t("common.back")} <ArrowRight size={14} />
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div className="bg-alabaster" data-testid="talent-registry-page">
      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-14 grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <p className="text-[10px] tracking-[0.3em] uppercase text-terracotta mb-4 inline-flex items-center gap-2">
            <GraduationCap size={14} /> {t("registry.overline_talent")}
          </p>
          <h1 className="font-serif text-5xl lg:text-7xl text-navy tracking-tight leading-[0.95]">
            {t("registry.title_talent")}
          </h1>
        </div>
        <div className="lg:col-span-4">
          <p className="text-navy/70 leading-relaxed">{t("registry.sub_talent")}</p>
          <Link to="/registry/couples" className="mt-6 inline-flex items-center gap-2 text-sm text-terracotta link-underline" data-testid="talent-switch-couples">
            {t("registry.switch_couples")}
          </Link>
        </div>
      </section>

      {/* FORM */}
      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 pb-24">
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={submit}
          className="bg-white border border-navy/8 rounded-[2rem] p-8 lg:p-12 space-y-10 shadow-[0_20px_60px_rgba(6,22,42,.07)]"
          data-testid="talent-registry-form"
        >
          {/* Personal */}
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-terracotta mb-6">01 — {lang === "fr" ? "Informations personnelles" : "Personal information"}</p>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label={f("first_name")}><input required value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className={inp} data-testid="tr-first-name" /></Field>
              <Field label={f("last_name")}><input required value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className={inp} data-testid="tr-last-name" /></Field>
              <Field label={f("email")}><input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inp} data-testid="tr-email" /></Field>
              <Field label={f("phone")}><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inp} data-testid="tr-phone" /></Field>
              <Field label={f("nationality")}><input value={form.nationality} onChange={(e) => set("nationality", e.target.value)} className={inp} placeholder={lang === "fr" ? "Canadienne, Sénégalaise, Française…" : "Canadian, Senegalese, French…"} data-testid="tr-nationality" /></Field>
              <Field label={f("date_of_birth")}><input type="date" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} className={inp} data-testid="tr-dob" /></Field>
              <Field label={f("country_of_residence")}><input required value={form.country_of_residence} onChange={(e) => set("country_of_residence", e.target.value)} className={inp} placeholder={lang === "fr" ? "Canada, France, Sénégal…" : "Canada, France, Senegal…"} data-testid="tr-country" /></Field>
              <Field label={f("city")}><input value={form.city} onChange={(e) => set("city", e.target.value)} className={inp} data-testid="tr-city" /></Field>
            </div>
          </div>

          {/* Education */}
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-terracotta mb-6">02 — {lang === "fr" ? "Formation" : "Education"}</p>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label={f("education_level")}>
                <select value={form.education_level} onChange={(e) => set("education_level", e.target.value)} className={sel} data-testid="tr-education-level">
                  <option value="bachelor">{f("education_bachelor")}</option>
                  <option value="master">{f("education_master")}</option>
                  <option value="phd">{f("education_phd")}</option>
                  <option value="other">{f("education_other")}</option>
                </select>
              </Field>
              <Field label={f("field_of_study")}><input value={form.field_of_study} onChange={(e) => set("field_of_study", e.target.value)} className={inp} data-testid="tr-field" /></Field>
              <Field label={f("institution")}><input value={form.institution} onChange={(e) => set("institution", e.target.value)} className={inp} data-testid="tr-institution" /></Field>
              <Field label={f("graduation_year")}><input type="number" min="1970" max="2030" value={form.graduation_year} onChange={(e) => set("graduation_year", e.target.value)} className={inp} data-testid="tr-grad-year" /></Field>
            </div>
          </div>

          {/* Experience */}
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-terracotta mb-6">03 — {lang === "fr" ? "Expérience professionnelle" : "Professional experience"}</p>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label={f("profession")}><input value={form.profession} onChange={(e) => set("profession", e.target.value)} className={inp} data-testid="tr-profession" /></Field>
              <Field label={f("years_of_experience")}><input type="number" min="0" max="60" value={form.years_of_experience} onChange={(e) => set("years_of_experience", e.target.value)} className={inp} data-testid="tr-yoe" /></Field>
              <div className="md:col-span-2">
                <Field label={f("professional_summary")}>
                  <textarea rows={4} value={form.professional_summary} onChange={(e) => set("professional_summary", e.target.value)} className={area} placeholder={lang === "fr" ? "Brève description de vos rôles, industries, réussites..." : "Brief description of roles, industries, achievements..."} data-testid="tr-summary" />
                </Field>
              </div>
            </div>
          </div>

          {/* Passport & languages */}
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-terracotta mb-6">04 — {lang === "fr" ? "Passeport & langues" : "Passport & languages"}</p>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label={f("has_passport")}>
                <div className="flex gap-2">
                  <button type="button" onClick={() => set("has_passport", true)} className={`flex-1 h-11 rounded-lg border text-sm transition ${form.has_passport ? "bg-navy text-alabaster border-navy" : "bg-white border-navy/15 text-navy hover:border-terracotta"}`} data-testid="tr-passport-yes">{f("yes")}</button>
                  <button type="button" onClick={() => set("has_passport", false)} className={`flex-1 h-11 rounded-lg border text-sm transition ${!form.has_passport ? "bg-navy text-alabaster border-navy" : "bg-white border-navy/15 text-navy hover:border-terracotta"}`} data-testid="tr-passport-no">{f("no")}</button>
                </div>
              </Field>
              <Field label={f("passport_expiry")}>
                <input type="date" value={form.passport_expiry} onChange={(e) => set("passport_expiry", e.target.value)} disabled={!form.has_passport} className={`${inp} disabled:opacity-40`} data-testid="tr-passport-expiry" />
              </Field>
              <Field label={f("french_level")}>
                <select value={form.french_level} onChange={(e) => set("french_level", e.target.value)} className={sel} data-testid="tr-french">
                  <option value="">—</option>
                  <option>A1</option><option>A2</option><option>B1</option><option>B2</option><option>C1</option><option>C2</option>
                  <option value="native">{lang === "fr" ? "Langue maternelle" : "Native"}</option>
                </select>
              </Field>
              <Field label={f("english_level")}>
                <select value={form.english_level} onChange={(e) => set("english_level", e.target.value)} className={sel} data-testid="tr-english">
                  <option value="">—</option>
                  <option>A1</option><option>A2</option><option>B1</option><option>B2</option><option>C1</option><option>C2</option>
                  <option value="native">{lang === "fr" ? "Langue maternelle" : "Native"}</option>
                </select>
              </Field>
            </div>
          </div>

          {/* Situation */}
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-terracotta mb-6">05 — {lang === "fr" ? "Objectif & situation" : "Goal & situation"}</p>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label={f("target_pathway")}>
                <select value={form.target_pathway} onChange={(e) => set("target_pathway", e.target.value)} className={sel} data-testid="tr-pathway">
                  <option value="PR">{f("pathway_pr")}</option>
                  <option value="Study">{f("pathway_study")}</option>
                  <option value="Work">{f("pathway_work")}</option>
                  <option value="Other">{f("pathway_other")}</option>
                </select>
              </Field>
              <Field label={f("financial_situation")}>
                <select value={form.financial_situation} onChange={(e) => set("financial_situation", e.target.value)} className={sel} data-testid="tr-fin">
                  <option value="constrained">{f("fin_constrained")}</option>
                  <option value="limited">{f("fin_limited")}</option>
                  <option value="ok">{f("fin_ok")}</option>
                </select>
              </Field>
              <div className="md:col-span-2">
                <Field label={f("notes")}>
                  <textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} className={area} data-testid="tr-notes" />
                </Field>
              </div>
            </div>
          </div>

          {/* Consent + submit */}
          <div className="pt-6 border-t border-navy/10">
            <label className="flex items-start gap-3 text-sm text-navy/75 cursor-pointer">
              <input type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1" data-testid="tr-consent" />
              <span>{t("registry.consent")}</span>
            </label>
            <button
              type="submit"
              disabled={busy || !form.consent}
              data-testid="tr-submit"
              className="mt-6 w-full h-12 rounded-full bg-navy text-alabaster hover:bg-terracotta transition text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? t("registry.submitting") : t("registry.submit")}
              <ShieldCheck size={14} />
            </button>
            <p className="mt-4 text-xs text-navy/50 flex items-center gap-2 justify-center">
              <ShieldCheck size={12} /> {t("registry.privacy")}
            </p>
          </div>
        </motion.form>
      </section>
    </div>
  );
}
